import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useDebounce } from '@/hooks/use-debounce';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps, type PaginationMeta } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { AlertCircle, CheckCircle2, Clock, RotateCcw, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { route } from 'ziggy-js';

interface RequestListItem {
    id: string;
    public_id: string | null;
    title: string;
    status: string;
    priority: string;
    template_name: string | null;
    requester: { id: string; name: string } | null;
    assignee: { id: string; name: string } | null;
    due_date: string | null;
    created_at: string | null;
}

interface AdminQueuePageProps extends PageProps {
    [key: string]: unknown;
    requests: RequestListItem[];
    pagination: PaginationMeta;
    filters: Record<string, string | number | undefined>;
    filter_options: {
        statuses: string[];
        priorities: string[];
        users: Array<{ id: string; name: string }>;
        templates: Array<{ id: string; name: string }>;
    };
    metrics: { open: number; in_progress: number; overdue: number; resolved_this_month: number };
}

const statusBadgeClassMap: Record<string, string> = {
    open: 'border-border bg-info-subtle text-info-subtle-foreground',
    in_progress: 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
    on_hold: 'border-border bg-warning-subtle text-warning-subtle-foreground',
    resolved: 'border-border bg-success-subtle text-success-subtle-foreground',
    closed: 'border-border bg-muted text-muted-foreground',
    rejected: 'border-border bg-danger-subtle text-danger-subtle-foreground',
};

const priorityBadgeClassMap: Record<string, string> = {
    low: 'border-border bg-muted text-muted-foreground',
    normal: 'border-border bg-info-subtle text-info-subtle-foreground',
    high: 'border-border bg-warning-subtle text-warning-subtle-foreground',
    urgent: 'border-border bg-danger-subtle text-danger-subtle-foreground',
};

export default function AdminServiceRequestsIndexPage() {
    const { props } = usePage<AdminQueuePageProps>();
    const { requests, pagination, filters, filter_options: filterOptions, metrics } = props;
    const { t } = useTranslations();
    const { applyFilters, loading } = useServerFilters(filters, '/admin/service-requests');

    const [search, setSearch] = useState(String(filters.search ?? ''));
    const debouncedSearch = useDebounce(search, 800);

    useEffect(() => {
        if (String(filters.search ?? '') === debouncedSearch) return;
        applyFilters({ search: debouncedSearch || undefined, page: 1 });
    }, [applyFilters, debouncedSearch, filters.search]);

    useEffect(() => {
        setSearch(String(filters.search ?? ''));
    }, [filters.search]);

    const metricCards = useMemo(
        () => [
            { label: t('service_requests.metric_open', 'Open'), value: metrics.open, icon: AlertCircle, color: 'text-info-subtle-foreground' },
            {
                label: t('service_requests.metric_in_progress', 'In Progress'),
                value: metrics.in_progress,
                icon: Clock,
                color: 'text-cat-purple-foreground',
            },
            { label: t('service_requests.metric_overdue', 'Overdue'), value: metrics.overdue, icon: TrendingUp, color: 'text-destructive' },
            {
                label: t('service_requests.metric_resolved_month', 'Resolved This Month'),
                value: metrics.resolved_this_month,
                icon: CheckCircle2,
                color: 'text-success-subtle-foreground',
            },
        ],
        [metrics, t],
    );

    const breadcrumbs = [{ title: t('service_requests.admin_queue_title', 'Service Request Queue'), href: '/admin/service-requests' }];

    const handleReset = () =>
        applyFilters({
            search: undefined,
            status: undefined,
            priority: undefined,
            assigned_to_user_id: undefined,
            requester_user_id: undefined,
            template_id: undefined,
            created_from: undefined,
            created_to: undefined,
            page: 1,
        });

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('service_requests.admin_queue_title', 'Service Request Queue')} />

            <PageHeader
                title={t('service_requests.admin_queue_title', 'Service Request Queue')}
                subtitle={t('service_requests.admin_queue_subtitle', 'Review and manage incoming service requests.')}
                actions={
                    <Button variant="outline" asChild>
                        <Link href={route('admin.service-request-templates.index')}>Templates</Link>
                    </Button>
                }
            />

            <PageContent className="space-y-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {metricCards.map((m) => {
                        const Icon = m.icon;
                        return (
                            <div key={m.label} className="bg-card rounded-xl border p-5">
                                <div className="flex items-center justify-between">
                                    <p className="text-muted-foreground text-sm">{m.label}</p>
                                    <Icon className={`h-4 w-4 ${m.color}`} />
                                </div>
                                <p className="mt-2 text-2xl font-bold">{m.value}</p>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-card rounded-xl border p-5">
                    <h2 className="mb-4 text-sm font-semibold">Filters</h2>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('service_requests.filter_search', 'Search requests...')}
                            />
                            <Select
                                value={String(filters.status ?? 'all')}
                                onValueChange={(v) => applyFilters({ status: v === 'all' ? undefined : v, page: 1 })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.filter_all_statuses', 'All statuses')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('service_requests.filter_all_statuses', 'All statuses')}</SelectItem>
                                    {filterOptions.statuses.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={String(filters.priority ?? 'all')}
                                onValueChange={(v) => applyFilters({ priority: v === 'all' ? undefined : v, page: 1 })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.filter_all_priorities', 'All priorities')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('service_requests.filter_all_priorities', 'All priorities')}</SelectItem>
                                    {filterOptions.priorities.map((p) => (
                                        <SelectItem key={p} value={p}>
                                            {p}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                            <Select
                                value={String(filters.assigned_to_user_id ?? 'all')}
                                onValueChange={(v) => applyFilters({ assigned_to_user_id: v === 'all' ? undefined : v, page: 1 })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.filter_all_assignees', 'All assignees')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('service_requests.filter_all_assignees', 'All assignees')}</SelectItem>
                                    {filterOptions.users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={String(filters.requester_user_id ?? 'all')}
                                onValueChange={(v) => applyFilters({ requester_user_id: v === 'all' ? undefined : v, page: 1 })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.filter_all_requesters', 'All requesters')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('service_requests.filter_all_requesters', 'All requesters')}</SelectItem>
                                    {filterOptions.users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={String(filters.template_id ?? 'all')}
                                onValueChange={(v) => applyFilters({ template_id: v === 'all' ? undefined : v, page: 1 })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t('service_requests.filter_all_templates', 'All templates')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('service_requests.filter_all_templates', 'All templates')}</SelectItem>
                                    {filterOptions.templates.map((tpl) => (
                                        <SelectItem key={tpl.id} value={tpl.id}>
                                            {tpl.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button variant="outline" onClick={handleReset}>
                                <RotateCcw className="mr-2 h-4 w-4" />
                                {t('service_requests.reset_filters', 'Reset filters')}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="bg-card overflow-hidden rounded-xl">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>{t('service_requests.template', 'Template')}</TableHead>
                                <TableHead>{t('service_requests.status', 'Status')}</TableHead>
                                <TableHead>{t('service_requests.priority', 'Priority')}</TableHead>
                                <TableHead>{t('service_requests.requester', 'Requester')}</TableHead>
                                <TableHead>{t('service_requests.assignee', 'Assignee')}</TableHead>
                                <TableHead>{t('service_requests.due_date', 'Due Date')}</TableHead>
                                <TableHead>{t('service_requests.created_at', 'Submitted')}</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} className="text-muted-foreground py-12 text-center">
                                        {t('service_requests.no_requests', 'No requests found.')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((req) => (
                                    <TableRow key={req.id}>
                                        <TableCell className="font-mono text-xs font-semibold">{req.public_id ?? req.id.slice(0, 8)}</TableCell>
                                        <TableCell>{req.template_name ?? req.title}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={statusBadgeClassMap[req.status] ?? ''}>
                                                {req.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={priorityBadgeClassMap[req.priority] ?? ''}>
                                                {req.priority}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{req.requester?.name ?? '—'}</TableCell>
                                        <TableCell>
                                            {req.assignee?.name ?? (
                                                <span className="text-muted-foreground text-sm">
                                                    {t('service_requests.unassigned', 'Unassigned')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">{req.due_date ?? '—'}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button asChild variant="outline" size="sm">
                                                <Link href={route('admin.service-requests.show', req.id)}>{t('service_requests.view', 'View')}</Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                <PaginationControls
                    pagination={pagination}
                    loading={loading}
                    onPageChange={(page) => applyFilters({ page })}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                />
            </PageContent>
        </AppLayout>
    );
}
